import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RefreshIcon from "../../../../assets/Fibo/icons/RefreshIcon.tsx";
import BriaButton from "../../../../components/common/BriaButton/BriaButton.tsx";
import BriaVideo, { BriaVideoProps } from "../../../../components/common/BriaVideo/BriaVideo";
import { AspectRatio } from "../../../../components/common/DropDowns/StaticDropdown.tsx";
import ImageError from "../../../../components/common/ImageError/ImageError";
import LoadingAnimation from "../../../../components/common/LoadingAnimation/LoadingAnimation";
import { PollingFileStatus } from "../../../../constants/ImageToImageConstants.ts";
import useImageUtils, { AsyncProps } from "../../../../utils/useImageUtils";
import styles from "./PlaygroundVideo.module.scss";

export type VideoErrorType = "unavailable";

export type PlaygroundVideoProps = {
	src: string;
	thumbnail?: string;
	className?: string;
	wrapperClassName?: string;
	asyncProps?: AsyncProps;
	loading?: boolean;
	onSuccessPulling?: (result?: any) => void;
	onErrorPulling?: (errorType?: string) => void;
	onTryAgain?: () => void;
	aspectRatio?: AspectRatio;
	error?: { message?: string; status?: number; type?: VideoErrorType };
} & Partial<BriaVideoProps>;

const PlaygroundVideo: React.FC<PlaygroundVideoProps> = ({
	src,
	thumbnail,
	className,
	asyncProps,
	loading,
	onSuccessPulling,
	onErrorPulling,
	onTryAgain,
	autoPlay = true,
	aspectRatio,
	showTransparentGridBackground = true,
	...rest
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "briaVideo" });
	const [currentVideoUrl, setCurrentVideoUrl] = useState<string>(src || "");
	const [isVideoLoading, setIsVideoLoading] = useState(loading || !src);
	const estimatedTime = useMemo(
		() => (asyncProps ? (asyncProps?.maxAttempts ?? 0) * (asyncProps?.sleepSecs ?? 0) : undefined),
		[asyncProps],
	);
	const [remainingSeconds, setRemainingSeconds] = useState<number | undefined>(estimatedTime);
	const [error, setError] = useState<PollingFileStatus | undefined>(undefined);
	const { pullUntilAvailableV2 } = useImageUtils();

	const formatTime = (seconds: number | undefined): string => {
		if (!seconds) return "";
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		if (mins === 0) {
			return `${secs} ${secs === 1 ? "second" : "seconds"}`;
		}
		const minsText = `${mins} ${mins === 1 ? "min" : "mins"}`;
		const secsText = `${secs} ${secs === 1 ? "second" : "seconds"}`;
		return `${minsText} ${secsText}`;
	};

	// Poll for video URL if status_url is available
	useEffect(() => {
		if (!asyncProps?.statusUrl || !src) return;

		const pollForVideo = async () => {
			try {
				setIsVideoLoading(true);
				const result = await pullUntilAvailableV2("", {
					...asyncProps,
					statusUrl: asyncProps?.statusUrl,
					isV2: true,
				});

				if (result?.video_url) {
					setCurrentVideoUrl(result.video_url);
					setIsVideoLoading(false);
					onSuccessPulling?.(result);
				}
			} catch (error: any) {
				setIsVideoLoading(false);
				setError(PollingFileStatus.UNAVAILABLE);
				onErrorPulling?.("unavailable");
			}
		};

		pollForVideo();
	}, [asyncProps?.statusUrl]);

	useEffect(() => {
		if (src && src !== currentVideoUrl) {
			setCurrentVideoUrl(src);
			setIsVideoLoading(false);
			setError(undefined);
		}
	}, [src]);

	useEffect(() => {
		if (asyncProps?.statusUrl) {
			setIsVideoLoading(true);
			setError(undefined);
		}
	}, [asyncProps?.statusUrl]);

	// Sync isVideoLoading with the loading prop when it changes
	useEffect(() => {
		if (loading !== undefined) {
			setIsVideoLoading(loading);
		}
	}, [loading]);

	useEffect(() => {
		if (!isVideoLoading) {
			setRemainingSeconds(estimatedTime);
			return;
		}

		setRemainingSeconds(estimatedTime);

		const interval = setInterval(() => {
			setRemainingSeconds((prev) => {
				const newValue = prev && prev > 0 ? prev - 10 : undefined;
				return newValue ? newValue : undefined;
			});
		}, 10000);

		return () => clearInterval(interval);
	}, [isVideoLoading, estimatedTime]);

	const handleTryAgain = () => {
		onTryAgain?.();
	};

	if (error === PollingFileStatus.UNAVAILABLE) {
		return (
			<Box className={styles.errorContainer}>
				<ImageError
					imageWidth="100%"
					imageHeight="100%"
					titleText={t(`errors.${error}.title`)}
					massageText={t(`errors.${error}.message`)}
					titleFontSize="14px"
					massageTextSize="14px"
					largeSvg
					aspectRatio={aspectRatio}
					actionButton={
						<BriaButton
							startIcon={<RefreshIcon className={styles.refreshIcon} />}
							buttonType="textSmall"
							onClick={handleTryAgain}
						>
							{t("tryAgain")}
						</BriaButton>
					}
				/>
			</Box>
		);
	}

	if (isVideoLoading) {
		return (
			<LoadingAnimation
				loading={true}
				singleModeLoader
				className={clsx(styles.videoContainer, className)}
				aspectRatio={aspectRatio}
				loadingStrings={
					remainingSeconds && remainingSeconds > 0
						? [t("estimatedGenerationTime", { time: formatTime(remainingSeconds) })]
						: undefined
				}
				loadingStringsTimeout={10000}
				progressBarTime={estimatedTime}
				showLoadingStrings={true}
			/>
		);
	}

	return (
		<BriaVideo
			thumbnail={thumbnail}
			{...rest}
			className={clsx(styles.videoContainer, className)}
			videoSrc={currentVideoUrl}
			autoPlay={autoPlay}
			showTransparentGridBackground={showTransparentGridBackground}
		/>
	);
};

export default observer(PlaygroundVideo);
